import { useState, useCallback, useEffect } from 'react';
import { ICD10_PATTERNS } from '../constants/mediscribe';

// Calculate age from DOB
export const calculateAge = (dob) => {
  if (!dob) return '';
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age.toString();
};

// Format date for display
export const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Custom hook for auto-extracting patient info from notes
export const usePatientExtraction = (notes, setPatientAge, setPatientGender, setPatientStatus, setSoldierRank) => {
  const extractPatientInfo = useCallback((text) => {
    // Try to extract rank
    const rankMatch = text.match(/\b(PVT|PV2|PFC|SPC|CPL|SGT|SSG|SFC|MSG|1SG|SGM|CSM|2LT|1LT|CPT|MAJ|LTC|COL|BG|MG|LTG|GEN)\b/i);
    if (rankMatch) setSoldierRank?.(prev => prev || rankMatch[1].toUpperCase());
    
    // Try to extract age
    const ageMatch = text.match(/(\d{1,2})\s*(?:yo|y\/o|year old|years old)/i);
    if (ageMatch) setPatientAge?.(prev => prev || ageMatch[1]);
    
    // Try to extract gender
    const genderMatch = text.match(/\b(male|female|M|F)\b/i);
    if (genderMatch) {
      const g = genderMatch[1].toUpperCase();
      setPatientGender?.(prev => prev || (g === 'MALE' || g === 'M' ? 'male' : 'female'));
    }
    
    // Try to extract status
    const statusMatch = text.match(/\b(AD|active duty|ADSM|veteran|retiree|dependent|civilian)\b/i);
    if (statusMatch) {
      const s = statusMatch[1].toLowerCase();
      setPatientStatus?.(prev => {
        if (prev) return prev;
        if (s === 'ad' || s === 'active duty' || s === 'adsm') return 'active_duty';
        return s;
      });
    }
  }, [setPatientAge, setPatientGender, setPatientStatus, setSoldierRank]);

  useEffect(() => {
    if (notes.length > 10) {
      extractPatientInfo(notes);
    }
  }, [notes, extractPatientInfo]);
};

// Custom hook for live ICD-10 code extraction
export const useLiveIcd10 = (notes) => {
  const [liveIcd10Suggestions, setLiveIcd10Suggestions] = useState([]);
  const [debouncedNotes, setDebouncedNotes] = useState(notes);

  // Debounce notes changes to reduce processing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNotes(notes);
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timer);
  }, [notes]);

  useEffect(() => {
    if (debouncedNotes.length < 5) {
      setLiveIcd10Suggestions([]);
      return;
    }

    const suggestions = [];
    const foundCodes = new Set();
    
    ICD10_PATTERNS.forEach(({ pattern, code, desc, category }) => {
      const regex = new RegExp(pattern.source, pattern.flags);
      if (regex.test(debouncedNotes) && !foundCodes.has(code)) {
        foundCodes.add(code);
        suggestions.push({ code, description: desc, category, auto: true });
      }
    });

    // Detect raw ICD-10 codes typed directly (e.g., "K58.9", "M54.50", "Z02.89")
    const rawCodeRegex = /\b([A-TV-Z]\d{2}(?:\.\d{1,4})?)\b/g;
    let match;
    while ((match = rawCodeRegex.exec(debouncedNotes)) !== null) {
      const rawCode = match[1];
      if (!foundCodes.has(rawCode)) {
        foundCodes.add(rawCode);
        const knownPattern = ICD10_PATTERNS.find(p => p.code === rawCode);
        suggestions.push({
          code: rawCode,
          description: knownPattern ? knownPattern.desc : `Dictated code`,
          category: knownPattern ? knownPattern.category : 'dictated',
          auto: true,
        });
      }
    }
    
    setLiveIcd10Suggestions(suggestions);
  }, [debouncedNotes]);

  return liveIcd10Suggestions;
};

// Custom hook for auto-extracting imaging and specialties from notes
// Enhanced: Better detection patterns, bilateral support, adds to existing lists
export const useImagingSpecialtyExtraction = (notes, setImagingOrders, setSpecialties, imagingOrders, specialties, visitType, dismissedSpecialties, dismissedImaging) => {
  const [debouncedNotes, setDebouncedNotes] = useState(notes);

  // Debounce notes changes (1.5 seconds after typing stops).
  // Feb 28, 2026 (v6.8.40 bugfix): when notes is cleared (Next Patient /
  // clearAll), sync debouncedNotes IMMEDIATELY. Previously the 1500 ms
  // timer kept the prior patient's dictation alive in debouncedNotes, and
  // when clearAll updated `visitType` it re-triggered the extraction effect
  // below which then RE-POPULATED specialties/imaging from the stale
  // (prior-patient) text — the "Referrals bleeding over" bug.
  useEffect(() => {
    if (!notes || notes.length < 10) {
      setDebouncedNotes(notes || '');
      return undefined;
    }
    const timer = setTimeout(() => {
      setDebouncedNotes(notes);
    }, 1500);
    return () => clearTimeout(timer);
  }, [notes]);

  useEffect(() => {
    if (debouncedNotes.length < 10) return;
    // Skip auto-extraction during Results Review — mentions of imaging/specialties
    // are about REVIEWING existing results, not ordering new ones
    if (visitType === 'results_review' || visitType === 'results_review_virtual') return;
    
    const text = debouncedNotes.toLowerCase();
    
    // Enhanced imaging patterns with bilateral, fingers, toes, sinuses
    const imagingPatterns = [
      // MRI patterns
      { pattern: /mri\s+(?:of\s+)?(?:the\s+)?(?:bilateral|both|b\/l)\s*(knee|shoulder|hip|ankle|wrist|elbow|foot|hand)s?/gi, type: 'MRI', bodyPartSuffix: ' (Bilateral)' },
      { pattern: /mri\s+(?:of\s+)?(?:the\s+)?(?:left|l)\s*(knee|shoulder|hip|ankle|wrist|elbow|foot|hand|finger|toe)s?/gi, type: 'MRI', bodyPartSuffix: ' (Left)' },
      { pattern: /mri\s+(?:of\s+)?(?:the\s+)?(?:right|r)\s*(knee|shoulder|hip|ankle|wrist|elbow|foot|hand|finger|toe)s?/gi, type: 'MRI', bodyPartSuffix: ' (Right)' },
      { pattern: /mri\s+(?:of\s+)?(?:the\s+)?(lumbar|l-?spine|lumbosacral)/gi, type: 'MRI', bodyPart: 'Lumbar Spine' },
      { pattern: /mri\s+(?:of\s+)?(?:the\s+)?(cervical|c-?spine)/gi, type: 'MRI', bodyPart: 'Cervical Spine' },
      { pattern: /mri\s+(?:of\s+)?(?:the\s+)?(thoracic|t-?spine)/gi, type: 'MRI', bodyPart: 'Thoracic Spine' },
      { pattern: /mri\s+(?:of\s+)?(?:the\s+)?(brain|head)/gi, type: 'MRI', bodyPart: 'Head/Brain' },
      { pattern: /mri\s+(?:of\s+)?(?:the\s+)?(abdomen|abdominal)/gi, type: 'MRI', bodyPart: 'Abdomen' },
      // Simple MRI mentions with body part in context
      { pattern: /(?:order|need|get|schedule|recommend)\s+(?:an?\s+)?mri/gi, type: 'MRI', bodyPart: null },
      // CT patterns
      { pattern: /ct\s+(?:scan\s+)?(?:of\s+)?(?:the\s+)?(sinus|sinuses)/gi, type: 'CT Sinus', bodyPart: 'Sinuses' },
      { pattern: /ct\s+(?:scan\s+)?(?:of\s+)?(?:the\s+)?(head|brain)/gi, type: 'CT Scan', bodyPart: 'Head/Brain' },
      { pattern: /ct\s+(?:scan\s+)?(?:of\s+)?(?:the\s+)?(chest)/gi, type: 'CT Scan', bodyPart: 'Chest' },
      { pattern: /ct\s+(?:scan\s+)?(?:of\s+)?(?:the\s+)?(abdomen|abdominal)/gi, type: 'CT Scan', bodyPart: 'Abdomen' },
      { pattern: /ct\s+(?:scan\s+)?(?:of\s+)?(?:the\s+)?(pelvis|pelvic)/gi, type: 'CT Scan', bodyPart: 'Pelvis' },
      { pattern: /ct\s+(?:scan\s+)?(?:of\s+)?(?:the\s+)?(abdomen.*pelvis|abd.*pel)/gi, type: 'CT Scan', bodyPart: 'Abdomen/Pelvis' },
      { pattern: /\bct\s+angiography|ct\s+angio(?:gram)?\b/gi, type: 'CT Angiography (CTA)', bodyPart: 'Heart/Cardiac' },
      // X-Ray patterns with bilateral
      { pattern: /x-?ray\s+(?:of\s+)?(?:the\s+)?(?:bilateral|both|b\/l)\s*(knee|shoulder|hip|ankle|wrist|elbow|foot|hand)s?/gi, type: 'X-Ray', bodyPartSuffix: ' (Bilateral)' },
      { pattern: /x-?ray\s+(?:of\s+)?(?:the\s+)?(?:left|l)\s*(knee|shoulder|hip|ankle|wrist|elbow|foot|hand|finger|toe)s?/gi, type: 'X-Ray', bodyPartSuffix: ' (Left)' },
      { pattern: /x-?ray\s+(?:of\s+)?(?:the\s+)?(?:right|r)\s*(knee|shoulder|hip|ankle|wrist|elbow|foot|hand|finger|toe)s?/gi, type: 'X-Ray', bodyPartSuffix: ' (Right)' },
      { pattern: /x-?ray\s+(?:of\s+)?(?:the\s+)?(chest|cxr)/gi, type: 'X-Ray', bodyPart: 'Chest' },
      { pattern: /x-?ray\s+(?:of\s+)?(?:the\s+)?(lumbar|l-?spine)/gi, type: 'X-Ray', bodyPart: 'Lumbar Spine' },
      { pattern: /x-?ray\s+(?:of\s+)?(?:the\s+)?(cervical|c-?spine)/gi, type: 'X-Ray', bodyPart: 'Cervical Spine' },
      { pattern: /x-?ray\s+(?:of\s+)?(?:the\s+)?(sinus|sinuses)/gi, type: 'X-Ray', bodyPart: 'Sinuses' },
      // Simple X-ray mentions - catch "get x-ray", "need xray", etc.
      { pattern: /(?:order|need|get|schedule|recommend)\s+(?:an?\s+)?x-?ray/gi, type: 'X-Ray', bodyPart: null },
      // Body part with imaging mentioned nearby
      { pattern: /(?:knee|shoulder|hip|ankle|back|spine|wrist|elbow|foot|hand)\s+(?:x-?ray|xr)/gi, type: 'X-Ray', bodyPartFromMatch: true },
      { pattern: /(?:knee|shoulder|hip|ankle|back|spine|wrist|elbow|foot|hand)\s+(?:mri)/gi, type: 'MRI', bodyPartFromMatch: true },
      // Ultrasound patterns
      { pattern: /ultrasound\s+(?:of\s+)?(?:the\s+)?(abdomen|abdominal)/gi, type: 'Ultrasound', bodyPart: 'Abdomen' },
      { pattern: /ultrasound\s+(?:of\s+)?(?:the\s+)?(pelvis|pelvic)/gi, type: 'Ultrasound', bodyPart: 'Pelvis' },
      { pattern: /ultrasound\s+(?:of\s+)?(?:the\s+)?(thyroid)/gi, type: 'Ultrasound', bodyPart: 'Thyroid' },
      // Other modalities
      { pattern: /carotid\s+(?:doppler|ultrasound|us|duplex|angio(?:gram|graphy)?)/gi, type: 'Ultrasound', bodyPart: 'Carotid Arteries (Bilateral)' },
      { pattern: /(?:dvt|venous)\s+(?:doppler|ultrasound|us|duplex)/gi, type: 'Ultrasound', bodyPart: 'Lower Extremity Veins / DVT (Bilateral)' },
      { pattern: /aaa\s+screen|aortic\s+ultrasound|abdominal\s+aorta\s+(?:ultrasound|us)/gi, type: 'Ultrasound', bodyPart: 'Aorta (Abdominal)' },
      { pattern: /(?:liver|hepatic)\s+(?:ultrasound|us|ct|mri|elastography|fibroscan)/gi, type: 'Ultrasound', bodyPart: 'Liver' },
      { pattern: /ruq\s+(?:ultrasound|us)|gallbladder\s+(?:ultrasound|us|hida)/gi, type: 'Ultrasound', bodyPart: 'Gallbladder' },
      { pattern: /renal\s+(?:ultrasound|us|ct|mri)|kidney\s+(?:ultrasound|us|ct)/gi, type: 'Ultrasound', bodyPart: 'Kidneys' },
      { pattern: /upper\s+gi|barium\s+swallow|gastric\s+(?:emptying|imaging)/gi, type: 'X-Ray', bodyPart: 'Stomach' },
      { pattern: /dexa|bone density/gi, type: 'DEXA Scan', bodyPart: 'Lumbar Spine' },
      { pattern: /echo|echocardiogram/gi, type: 'Echocardiogram', bodyPart: 'Heart/Cardiac' },
      { pattern: /mammo/gi, type: 'Mammography', bodyPart: 'Chest' },
    ];
    
    // Specialty patterns - enhanced with more trigger phrases
    const specialtyPatterns = [
      // Orthopedics
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(orthopedic|ortho)/gi, specialty: 'Orthopedic Surgery' },
      { pattern: /needs?\s+(?:to see\s+)?(?:an?\s+)?(orthopedic|ortho)/gi, specialty: 'Orthopedic Surgery' },
      { pattern: /consult\s+(?:with\s+)?(?:the\s+)?(orthopedic|ortho)/gi, specialty: 'Orthopedic Surgery' },
      { pattern: /ortho(?:pedic)?\s+(?:consult|referral|eval)/gi, specialty: 'Orthopedic Surgery' },
      // Cardiology
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(cardiology|cardiologist)/gi, specialty: 'Cardiology' },
      { pattern: /cardio(?:logy)?\s+(?:consult|referral|eval)/gi, specialty: 'Cardiology' },
      // Neurology
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(neurology|neurologist)/gi, specialty: 'Neurology' },
      { pattern: /neuro(?:logy)?\s+(?:consult|referral|eval)/gi, specialty: 'Neurology' },
      // Dermatology
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(dermatology|dermatologist|derm)/gi, specialty: 'Dermatology' },
      { pattern: /derm(?:atology)?\s+(?:consult|referral|eval)/gi, specialty: 'Dermatology' },
      // GI
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(gastro|gi|gastroenterology)/gi, specialty: 'Gastroenterology' },
      { pattern: /gi\s+(?:consult|referral|eval)/gi, specialty: 'Gastroenterology' },
      // Psychiatry / BH
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(psychiatry|psychiatrist|psych)/gi, specialty: 'Psychiatry' },
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(behavioral health|bh)/gi, specialty: 'Behavioral Health' },
      { pattern: /bh\s+(?:consult|referral|eval)/gi, specialty: 'Behavioral Health' },
      // PT
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(physical therapy|pt|physio)/gi, specialty: 'Physical Therapy' },
      { pattern: /pt\s+(?:consult|referral|eval)/gi, specialty: 'Physical Therapy' },
      { pattern: /needs?\s+physical therapy/gi, specialty: 'Physical Therapy' },
      // Nutrition
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(nutrition|dietitian|dietetics)/gi, specialty: 'Nutrition/Dietetics' },
      // Pain Management
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(pain management|pain clinic)/gi, specialty: 'Pain Management' },
      { pattern: /pain\s+(?:management|clinic)\s+(?:consult|referral)/gi, specialty: 'Pain Management' },
      // Ophthalmology
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(ophthalmology|ophthalmologist|eye)/gi, specialty: 'Ophthalmology' },
      // ENT
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(ent|otolaryngology)/gi, specialty: 'Otolaryngology (ENT)' },
      { pattern: /ent\s+(?:consult|referral|eval)/gi, specialty: 'Otolaryngology (ENT)' },
      // Urology
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(urology|urologist)/gi, specialty: 'Urology' },
      // Pulmonology
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(pulmonology|pulmonologist)/gi, specialty: 'Pulmonology' },
      // Rheumatology
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(rheumatology|rheumatologist)/gi, specialty: 'Rheumatology' },
      // Sports Medicine
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(sports medicine)/gi, specialty: 'Sports Medicine' },
      // PM&R
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(pm&r|pmr|physical medicine)/gi, specialty: 'Physical Medicine & Rehab (PM&R)' },
      // Podiatry
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(podiatry|podiatrist)/gi, specialty: 'Podiatry' },
      // OB/GYN
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(obgyn|ob\/gyn|gynecology)/gi, specialty: 'Obstetrics/Gynecology (OB/GYN)' },
      // Endocrinology
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(endocrin|endo)/gi, specialty: 'Endocrinology' },
      // Surgery
      { pattern: /refer(?:ral)?\s+(?:to\s+)?(?:the\s+)?(general surgery|surgeon)/gi, specialty: 'General Surgery' },
    ];
    
    // Extract imaging orders
    const foundImaging = [];
    imagingPatterns.forEach(({ pattern, type, bodyPart, bodyPartSuffix, bodyPartFromMatch }) => {
      let match;
      const regex = new RegExp(pattern.source, pattern.flags);
      while ((match = regex.exec(text)) !== null) {
        let foundBodyPart = bodyPart;
        if (bodyPartSuffix && match[1]) {
          // Capitalize first letter of matched body part
          const part = match[1].charAt(0).toUpperCase() + match[1].slice(1);
          // Handle plurals (knees -> Knee)
          const singularPart = part.replace(/s$/, '');
          // Special handling for fingers/toes
          const finalPart = singularPart === 'Finger' ? 'Fingers' : singularPart === 'Toe' ? 'Toes' : singularPart;
          foundBodyPart = finalPart + bodyPartSuffix;
        } else if (bodyPartFromMatch && match[0]) {
          // Extract body part from the match itself
          const partMatch = match[0].match(/knee|shoulder|hip|ankle|back|spine|wrist|elbow|foot|hand/i);
          if (partMatch) {
            const part = partMatch[0].charAt(0).toUpperCase() + partMatch[0].slice(1).toLowerCase();
            foundBodyPart = part === 'Back' || part === 'Spine' ? 'Lumbar Spine' : part;
          }
        }
        // Add even if no body part (user can fill in)
        const exists = foundImaging.some(i => i.type === type && i.bodyPart === foundBodyPart);
        if (!exists) {
          foundImaging.push({ type, bodyPart: foundBodyPart || '' });
        }
      }
    });
    
    // Smart merge: Add new imaging without duplicating.
    // Mar 3, 2026 — also skip anything the provider explicitly dismissed
    // this encounter (dismissedImaging Set, keyed by "type|bodyPart" lc).
    const dismissedImagingSet = dismissedImaging instanceof Set ? dismissedImaging : new Set();
    const filteredImaging = foundImaging.filter((f) => {
      const key = `${(f.type || '').toLowerCase()}|${(f.bodyPart || '').toLowerCase()}`;
      return !dismissedImagingSet.has(key);
    });
    if (filteredImaging.length > 0) {
      setImagingOrders(prev => {
        // If only one empty slot, replace with found items
        const hasOnlyEmpty = prev.length === 1 && !prev[0].type;
        if (hasOnlyEmpty) return filteredImaging;
        
        // Otherwise, add only new items not already in list
        const newItems = filteredImaging.filter(found => 
          !prev.some(existing => existing.type === found.type && existing.bodyPart === found.bodyPart)
        );
        return newItems.length > 0 ? [...prev.filter(p => p.type), ...newItems] : prev;
      });
    }
    
    // Extract specialties
    const foundSpecialties = new Set();
    specialtyPatterns.forEach(({ pattern, specialty }) => {
      const regex = new RegExp(pattern.source, pattern.flags);
      if (regex.test(text)) {
        foundSpecialties.add(specialty);
      }
    });
    
    // Smart merge: Add new specialties without duplicating.
    // Mar 3, 2026 — also skip anything the provider explicitly dismissed
    // this encounter (dismissedSpecialties Set, keyed by lowercase name).
    const dismissedSpecSet = dismissedSpecialties instanceof Set ? dismissedSpecialties : new Set();
    const filteredSpecs = [...foundSpecialties].filter((s) => !dismissedSpecSet.has(s.toLowerCase()));
    if (filteredSpecs.length > 0) {
      setSpecialties(prev => {
        const hasOnlyEmpty = prev.length === 1 && !prev[0];
        if (hasOnlyEmpty) return filteredSpecs;
        
        const newSpecs = filteredSpecs.filter(s => !prev.includes(s));
        return newSpecs.length > 0 ? [...prev.filter(p => p), ...newSpecs] : prev;
      });
    }
  }, [debouncedNotes, setImagingOrders, setSpecialties, visitType, dismissedSpecialties, dismissedImaging]);
};

// Copy to clipboard helper
export const copyToClipboard = async (text) => {
  // Guard against null/undefined text
  if (!text) {
    console.warn('copyToClipboard: No text provided');
    return false;
  }
  
  console.log('copyToClipboard: Starting copy, text length:', text.length);
  
  const cleanText = String(text)
    .replace(/\*\*/g, '')           // Remove bold markdown
    .replace(/#{1,6}\s/g, '')       // Remove heading markdown
    .replace(/`/g, '')              // Remove code backticks
    .replace(/^["']|["']$/g, '')    // Remove leading/trailing quotes
    .replace(/^"+|"+$/gm, '')       // Remove multiple quotes at start/end of lines
    .replace(/"{2,}/g, '"')         // Replace multiple quotes with single
    .trim();
  
  if (!cleanText) {
    console.warn('copyToClipboard: Empty text after cleaning');
    return false;
  }
  
  console.log('copyToClipboard: Clean text length:', cleanText.length);

  // MHS Genesis (Cerner Powerchart) rich-text fields IGNORE plain-text `\n`
  // line breaks on paste — the result is one big run-on smashed together
  // with no SUMMARY / HPI / A/P headers visible. Bug reported Feb 16, 2026.
  //
  // Fix: write BOTH text/html (with explicit <br> tags and Times New Roman
  // styling) AND text/plain (with Windows-style \r\n) to the clipboard.
  // Genesis's RTF receiver picks the HTML path, preserves line breaks AND
  // matches its native paragraph styling. Plain-text apps (Notepad, Word,
  // textareas) pick the text/plain path, preserve \r\n.
  const htmlEscaped = cleanText
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Convert each line to its own paragraph; preserve blank lines as empty paragraphs
  const htmlBody = htmlEscaped
    .split(/\n/)
    .map(line => line.length === 0
      ? '<p style="margin:0;line-height:1.4;">&nbsp;</p>'
      : `<p style="margin:0;line-height:1.4;">${line}</p>`
    )
    .join('');
  const htmlPayload = `<div style="font-family:'Times New Roman',Georgia,serif;font-size:12pt;color:#000;">${htmlBody}</div>`;
  // Windows line endings work universally — Notepad, Word, Genesis plain
  // text fields, mobile inputs. Pure \n is fine for unix-y apps too.
  const plainPayload = cleanText.replace(/\r\n/g, '\n').replace(/\n/g, '\r\n');

  // Try modern Clipboard API with BOTH HTML and plain text (ClipboardItem)
  try {
    if (navigator.clipboard && window.isSecureContext && typeof ClipboardItem !== 'undefined') {
      console.log('copyToClipboard: Using Clipboard API with text/html + text/plain');
      const item = new ClipboardItem({
        'text/html':  new Blob([htmlPayload],  { type: 'text/html'  }),
        'text/plain': new Blob([plainPayload], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([item]);
      console.log('copyToClipboard: Clipboard API (rich) succeeded');
      return true;
    }
  } catch (err) {
    console.warn('copyToClipboard: Rich Clipboard API failed, falling back to writeText:', err.message);
  }

  // Fallback A: modern API but text-only (Safari sometimes blocks
  // ClipboardItem outside a direct user gesture)
  try {
    if (navigator.clipboard && window.isSecureContext) {
      console.log('copyToClipboard: Using Clipboard API writeText fallback');
      await navigator.clipboard.writeText(plainPayload);
      console.log('copyToClipboard: writeText succeeded');
      return true;
    }
  } catch (err) {
    console.warn('copyToClipboard: writeText failed:', err.message);
  }
  
  // Fallback B: execCommand textarea — uses \r\n so Genesis preserves breaks
  try {
    console.log('copyToClipboard: Trying execCommand fallback');
    const textArea = document.createElement('textarea');
    textArea.value = plainPayload;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      console.log('copyToClipboard: execCommand succeeded');
      return true;
    } else {
      console.log('copyToClipboard: execCommand returned false');
    }
  } catch (err) {
    console.warn('copyToClipboard: execCommand fallback failed:', err.message);
  }
  
  // Last resort: Show text in a prompt for manual copy
  console.log('copyToClipboard: All methods failed, showing prompt');
  window.prompt('Copy failed. Select and copy this text manually:', cleanText.substring(0, 500));
  return false;
};
